import { destinationProvider } from '@/lib/search/destinations';
import { LAYER_ORDER, type SearchContext, type SearchProvider, type SearchResult } from '@/lib/search/types';

/**
 * The active layers, cheapest first.
 *
 * Adding the record layer ("find Jane's CPR renewal") means writing a provider
 * that returns a promise and listing it here. `runSearch` already reports
 * partial results as each layer lands, and the UI already groups by layer, so
 * neither has to change.
 */
export const PROVIDERS: SearchProvider[] = [destinationProvider];

/**
 * Run every layer against `query`. Synchronous layers are handed to `onResults`
 * before any await, so an instant answer is never held hostage to a slow one;
 * async layers merge in as they resolve.
 *
 * Returns the final merged list, sorted by layer then score.
 */
export async function runSearch(
  query: string,
  ctx: SearchContext,
  onResults?: (partial: SearchResult[]) => void,
): Promise<SearchResult[]> {
  if (!query.trim()) {
    onResults?.([]);
    return [];
  }

  const settled: SearchResult[] = [];
  const pending: Promise<SearchResult[]>[] = [];

  for (const provider of PROVIDERS) {
    const out = provider.search(query, ctx);
    if (out instanceof Promise) pending.push(out);
    else settled.push(...out);
  }

  onResults?.(sortResults(settled));
  if (pending.length === 0) return sortResults(settled);

  // A failing layer must not blank the layers that already answered.
  const rest = await Promise.all(pending.map((p) => p.catch(() => [] as SearchResult[])));
  const all = sortResults([...settled, ...rest.flat()]);
  onResults?.(all);
  return all;
}

export function sortResults(results: SearchResult[]): SearchResult[] {
  return [...results].sort(
    (a, b) =>
      LAYER_ORDER.indexOf(a.layer) - LAYER_ORDER.indexOf(b.layer) ||
      b.score - a.score ||
      a.title.localeCompare(b.title),
  );
}
